// Un mensaje de la conversación, con todo lo que lo hace verificable.
//
// Es la unidad donde cuelga lo demás: lo que el Brain ejecutó, la confirmación
// que espera y lo que costó el turno. Vive aparte porque la página compone y
// esto pinta.
//
// **La presentación es la del prototipo** (`dont-merge-front-vision`), y no es
// cosmética: lo que escribe el usuario va en burbuja a la derecha, y lo que
// responde el Brain va a todo el ancho y **pasado por Markdown**. Hasta el
// 2026-08-25 se pintaba en crudo, así que sus respuestas se leían con los
// asteriscos a la vista: «He creado el agente \*\*Recepción\*\*».
//
// El degradado de la burbuja es el que ya usa esta pantalla en su botón de
// enviar. El prototipo usaba una clase suya, `ds-gradient-brand`, que no existe
// en este repositorio: traerla sería traer medio sistema de diseño.

import { Loader2, Paperclip } from 'lucide-react'
import { splitAttachment } from '@/lib/attachment-message'
import { Button } from '@/components/ui/button'
import ReactMarkdown from 'react-markdown'
import { PROSE_BRAIN } from './mdComponents'
import remarkGfm from 'remark-gfm'
import { useTranslation } from 'react-i18next'
import { ConfirmacionPendiente } from './ConfirmacionPendiente'
import { QuestionCard } from './QuestionCard'
import { FileProposals, type ResolvedProposals } from './FileProposals'
import { HerramientasUsadas } from './HerramientasUsadas'
import { AnswerSteps, BrainSteps } from './BrainSteps'
import { CosteDelTurno } from './CosteDelTurno'
import { EnQueSeBaso } from './EnQueSeBaso'
import { PlanBar } from './PlanBar'
import type {
  BrainToolCall,
  CosteDelTurno as Coste,
  EnQueSeBaso as Base,
  PersonQuestion,
  RunEvent,
  PlanStep,
} from '@/services/brainApi'
import type { Pendiente } from '@/services/workspacesService'

/** What to do with a document passed through the paperclip (brain-multimodal,
 *  D6): read it for this conversation only, or keep it in the Knowledge. */
export type FileChoice = 'read' | 'keep'

export interface MensajeDelBrain {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: Date
  /** Que documentos uso y por que ese primero. Lo escribe el servidor.
   *
   *  Sustituye a un campo `sources` que estaba declarado desde el prototipo y
   *  **no lo llenaba nadie**: el bloque de fuentes no se pinto ni una vez.
   */
  enQueSeBaso?: Base
  /** De donde salio lo que dice. Sin esto, «tienes 4 documentos» es
   *  indistinguible de un 4 inventado. */
  herramientas?: BrainToolCall[]
  /** La operacion que el Brain se detuvo a preguntar, si la hay. */
  confirmacion?: NonNullable<BrainToolCall['accion']>
  /** La pregunta que la origino: confirmar es reenviarla con la accion aprobada. */
  peticionOriginal?: string
  confirmacionResuelta?: 'confirmada' | 'cancelada'
  /** The questions the Brain stopped to ask, with their options (D7). They stay
   *  in the history once answered; the card only while `questionOpen`. */
  questions?: PersonQuestion[]
  questionOpen?: boolean
  /** Lo que el agente propone recordar del archivo que se acaba de subir
   *  (5.2). Va colgado del mensaje y no de la pantalla **porque lo que se
   *  decide forma parte del historial**, igual que la confirmacion. */
  fileProposals?: Pendiente[]
  /** Asking what to do with a document: `pending` until chosen (D6). */
  fileChoice?: 'pending' | FileChoice
  fileProposalsResolved?: ResolvedProposals
  coste?: Coste
  isGenerating?: boolean
  /** Lo que va haciendo mientras piensa: solo en el mensaje de «pensando». */
  liveSteps?: RunEvent[]
  /** El plan del encargo, mientras piensa, si el trabajo lleva varios pasos. */
  plan?: PlanStep[]
  /** Lo que hizo para dar esta respuesta, ya terminada: lo que pensó y cada
   *  consulta, en orden. Sustituye a la lista de consultas de abajo, que
   *  enseñaría las mismas dos veces. */
  steps?: RunEvent[]
}

interface Props {
  message: MensajeDelBrain
  nombresHerramientas: Map<string, string>
  onConfirmar: (mensaje: MensajeDelBrain) => void
  onCancelar: (mensaje: MensajeDelBrain) => void
  ocupado: boolean
  onResolveProposal?: (
    messageId: string,
    proposal: Pendiente,
    decision: 'aprobar' | 'rechazar',
    reason: string,
  ) => void
  resolvingProposal?: string
  /** Answering the Brain's question: the chosen option, or what was written. */
  onAnswer?: (mensaje: MensajeDelBrain, answer: string) => void
  onFileChoice?: (mensaje: MensajeDelBrain, choice: FileChoice) => void
}

export function Mensaje({
  message,
  nombresHerramientas,
  onConfirmar,
  onCancelar,
  ocupado,
  onResolveProposal,
  resolvingProposal,
  onAnswer,
  onFileChoice,
}: Props) {
  const { t } = useTranslation()

  if (message.role === 'user') {
    // What the paperclip brought travels inside the message for the Brain; the
    // bubble shows only what was attached (brain-multimodal, 3.5).
    const { text, attachment } = splitAttachment(message.content)
    return (
      <div className="flex flex-col items-end gap-2">
        <div className="max-w-[85%] bg-primary rounded-lg rounded-tr-md px-4 py-2.5 text-sm leading-relaxed text-white shadow-sm">
          <span className="whitespace-pre-wrap break-words">{text}</span>
          {attachment && (
            <span className="mt-1 flex items-center gap-1 text-xs opacity-80">
              <Paperclip className="h-3 w-3 shrink-0" aria-hidden />
              {t('brain.archivo.adjuntado', { nombre: attachment })}
            </span>
          )}
        </div>
      </div>
    )
  }

  if (message.isGenerating) {
    return (
      <div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">{t('brain.thinking')}</span>
        </div>
        {message.plan && <div className="mt-2"><PlanBar steps={message.plan} /></div>}
        {message.liveSteps && <BrainSteps events={message.liveSteps} names={nombresHerramientas} />}
      </div>
    )
  }

  // El aviso existe para las **afirmaciones**: «tienes 4 documentos» sin haber
  // mirado es indistinguible de un 4 inventado. En un saludo o en una pregunta
  // sobre el propio Brain no hay nada que comprobar, y ponerlo ahi lo convierte
  // en ruido que se deja de leer justo cuando importa.
  // Y **mirar documentos tambien es consultar**, aunque no pase por una
  // herramienta: la busqueda vive dentro del turno, no en una llamada. Sin esta
  // mitad, una respuesta sacada de la tarifa se acusaba a si misma de no haber
  // mirado nada justo encima del bloque que dice en que se baso.
  const afirmaAlgo = /\d|tienes|hay |son |tiene |esta |estan /i.test(message.content)
  const consulto =
    (message.herramientas?.length ?? 0) > 0 || (message.enQueSeBaso?.documentos.length ?? 0) > 0
  const sinConsultar = !consulto && afirmaAlgo

  const conPasos = Boolean(message.steps?.length)
  const asked = message.questions?.length ? message.questions : null

  return (
    <div className="w-full min-w-0 text-sm leading-relaxed text-foreground">
      {conPasos && (
        <AnswerSteps events={message.steps!} calls={message.herramientas} names={nombresHerramientas} />
      )}

      {message.content && (
        <div className={PROSE_BRAIN}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
        </div>
      )}

      {message.fileChoice === 'pending' && onFileChoice && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" disabled={ocupado} onClick={() => onFileChoice(message, 'read')}>
            {t('brain.archivo.solo_aqui')}
          </Button>
          <Button size="sm" variant="secondary" disabled={ocupado} onClick={() => onFileChoice(message, 'keep')}>
            {t('brain.archivo.guardar')}
          </Button>
        </div>
      )}
      {(message.fileChoice === 'read' || message.fileChoice === 'keep') && (
        <p className="mt-1 text-xs text-muted-foreground">
          {t(message.fileChoice === 'read' ? 'brain.archivo.solo_aqui' : 'brain.archivo.guardar')}
        </p>
      )}

      {sinConsultar && (
        // Una respuesta sin fuentes se veia igual que una con ellas. Y el
        // 2026-08-24 el Brain conto documentos sin mirarlos, con un numero ya
        // desfasado: si la ausencia no se enseña, no se puede distinguir.
        <p className="mt-3 text-xs text-warning">
          {t('brain.sin_consultar')}
        </p>
      )}

      {message.confirmacion && !message.confirmacionResuelta && (
        <ConfirmacionPendiente
          accion={message.confirmacion}
          onConfirmar={() => onConfirmar(message)}
          onCancelar={() => onCancelar(message)}
          ocupado={ocupado}
        />
      )}

      {asked && message.questionOpen && onAnswer && (
        <QuestionCard questions={asked} onAnswer={(answer) => onAnswer(message, answer)} busy={ocupado} />
      )}

      {asked && !message.questionOpen && (
        // Answered: what was asked stays written, small, without its options
        // -- they can be whole sentences, and the answer is right below, as
        // the person's next message.
        <div className="mt-3 space-y-1 text-xs text-muted-foreground">
          {asked.map((q) => (
            <p key={q.text}>{t('brain.pregunto', { pregunta: q.text })}</p>
          ))}
        </div>
      )}

      {message.confirmacionResuelta === 'cancelada' && (
        <p className="mt-2 text-xs text-muted-foreground">{t('brain.cancelado')}</p>
      )}

      {message.fileProposals && onResolveProposal && (
        <FileProposals
          proposals={message.fileProposals}
          resolved={message.fileProposalsResolved}
          busy={resolvingProposal}
          onResolve={(proposal, decision, reason) =>
            onResolveProposal(message.id, proposal, decision, reason)
          }
        />
      )}

      {!conPasos && message.herramientas && message.herramientas.length > 0 && (
        <HerramientasUsadas llamadas={message.herramientas} nombres={nombresHerramientas} />
      )}

      {message.coste && <CosteDelTurno coste={message.coste} />}

      {message.enQueSeBaso && <EnQueSeBaso base={message.enQueSeBaso} />}
    </div>
  )
}
